import React, { useMemo, useState, useRef, useEffect, useCallback } from 'react';
import { Edit3, Check, X } from 'lucide-react';

interface YamlCodeViewerProps {
  value: string;
  className?: string;
  editable?: boolean;
  onChange?: (value: string) => void;
  onSave?: (value: string) => void;
  onCancel?: () => void;
  placeholder?: string;
  readOnly?: boolean;
}

// Define CSS classes for syntax highlighting
const SYNTAX_STYLES = `
  /* Light mode colors (inspired by VS Code's default light theme) */
  .yaml-comment { color: #595f56; font-style: italic; }
  .yaml-key { color: #2e5cc0; }
  .yaml-string { color: #c96c47; }
  .yaml-boolean { color: #0e5692; }
  .yaml-null { color: #4a667c; }
  .yaml-number { color: #20a648; }
  .yaml-array { color: #911a87; }
  .yaml-separator { color: #7c7c7c; } 
  .yaml-value { color: #585463; }
  .yaml-keyword { color: #2682ce; }
  
  /* Dark mode colors (inspired by VS Code's default dark theme) */
  @media (prefers-color-scheme: dark) {
    .dark .yaml-comment { color: #6A9955; }
    .dark .yaml-key { color: #af84ff; }
    .dark .yaml-string { color: #f17f52; }
    .dark .yaml-boolean { color: #569CD6; }
    .dark .yaml-null { color: #569CD6; }
    .dark .yaml-number { color: #85e352; }
    .dark .yaml-array { color: #C586C0; }
    .dark .yaml-separator { color: #bbbbbb; }
    .dark .yaml-value { color: #bcf5d2; }
    .dark .yaml-keyword { color: #569CD6; }
  }

  /* Editor specific styles */
  .yaml-editor-textarea {
    resize: none;
    outline: none;
    border: none;
    background: transparent;
    color: inherit;
    font-family: inherit;
    font-size: inherit;
    line-height: inherit;
    padding: 0;
    margin: 0;
    width: 100%;
    height: 100%;
    white-space: pre;
    overflow: visible;
    tab-size: 2;
  }

  .yaml-editor-textarea::placeholder {
    color: #9ca3af;
  }

  .dark .yaml-editor-textarea::placeholder {
    color: #6b7280;
  }
`;

// YAML syntax highlighting patterns with CSS classes
const YAML_PATTERNS = [
  // Comments
  { pattern: /#.*$/gm, className: 'yaml-comment' },
  // Keys (before colon)
  { pattern: /^(\s*)([^:\s#][^:#]*?)(\s*):/gm, className: 'yaml-key', group: 2 },
  // String values (quoted)
  { pattern: /:\s*(['"])((?:\\.|(?!\1)[^\\])*)\1/g, className: 'yaml-string', group: 2 },
  // Boolean values
  { pattern: /:\s*(true|false|True|False|TRUE|FALSE)\b/g, className: 'yaml-boolean', group: 1 },
  // Null values
  { pattern: /:\s*(null|Null|NULL|~)\b/g, className: 'yaml-null', group: 1 },
  // Numbers
  { pattern: /:\s*(-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)\b/g, className: 'yaml-number', group: 1 },
  // Array indicators
  { pattern: /^(\s*)-\s/gm, className: 'yaml-array', group: 1 },
  // Document separators
  { pattern: /^(---|\.\.\.)$/gm, className: 'yaml-separator' },
  // Unquoted string values (fallback)
  { pattern: /:\s*([^#\n\r]+?)(?:\s*#.*)?$/gm, className: 'yaml-value', group: 1 },
];

// YAML keywords for enhanced highlighting
const YAML_KEYWORDS = [
  'inputKey', 'transforms', 'events', 'connections', 'mappings', 'filters', 'enrichments',
  'expressions', 'spreading', 'inputKey', 'outputKey', 'conditions', 'actions'
];

const highlightYaml = (code: string): string => {
  let highlighted = code;
  
  // First, escape HTML to prevent XSS
  highlighted = highlighted
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  // Apply syntax highlighting patterns
  YAML_PATTERNS.forEach(({ pattern, className, group }) => {
    highlighted = highlighted.replace(pattern, (match, ...groups) => {
      const targetGroup = group ? groups[group - 1] : match;
      const beforeGroup = group ? match.substring(0, match.indexOf(targetGroup)) : '';
      const afterGroup = group ? match.substring(match.indexOf(targetGroup) + targetGroup.length) : '';
      
      return `${beforeGroup}<span class="${className}">${targetGroup}</span>${afterGroup}`;
    });
  });

  // Highlight YAML keywords
  YAML_KEYWORDS.forEach(keyword => {
    const keywordPattern = new RegExp(`\\b(${keyword})\\b`, 'g');
    highlighted = highlighted.replace(keywordPattern, `<span class="yaml-keyword">$1</span>`);
  });

  return highlighted;
};

const YamlCodeViewer: React.FC<YamlCodeViewerProps> = ({
  value,
  className = '',
  editable = false,
  onChange,
  onSave,
  onCancel,
  placeholder = 'Enter YAML content...',
  readOnly = false,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Update edit value when prop value changes
  useEffect(() => {
    if (!isEditing) {
      setEditValue(value);
    }
  }, [value, isEditing]);

  // Process the YAML content for display
  const processedContent = useMemo(() => {
    const displayValue = isEditing ? editValue : value;
    if (!displayValue) return '';
    
    // Remove any leading whitespace from the first line to fix indentation issue
    const trimmedValue = displayValue.replace(/^\s+/, '');
    
    return highlightYaml(trimmedValue);
  }, [value, editValue, isEditing]);

  // Calculate line numbers
  const lines = useMemo(() => {
    const displayValue = isEditing ? editValue : value;
    if (!displayValue) return [];
    const trimmedValue = displayValue.replace(/^\s+/, '');
    return trimmedValue.split('\n');
  }, [value, editValue, isEditing]);

  // Generate indentation guides
  const generateIndentationGuides = (line: string, lineIndex: number) => {
    const match = line.match(/^(\s*)/);
    const indentLevel = match ? Math.floor(match[1].length / 2) : 0;
    const guides = [];
    
    for (let i = 0; i < indentLevel; i++) {
      guides.push(
        <div
          key={`${lineIndex}-${i}`}
          className="absolute border-l border-gray-400 dark:border-gray-500 opacity-50"
          style={{
            left: `${(i * 2 + 1) * 0.5}rem`,
            top: 0,
            bottom: 0,
            width: '1px',
          }}
        />
      );
    }
    
    return guides;
  };

  // Handle edit mode toggle
  const handleEditToggle = useCallback(() => {
    if (readOnly) return;
    
    if (isEditing) {
      // Cancel editing
      setEditValue(value);
      setIsEditing(false);
      onCancel?.();
    } else {
      // Start editing
      setIsEditing(true);
      // Focus textarea after state update
      setTimeout(() => {
        textareaRef.current?.focus();
      }, 0);
    }
  }, [isEditing, value, readOnly, onCancel]);

  // Handle save
  const handleSave = useCallback(() => {
    onChange?.(editValue);
    onSave?.(editValue);
    setIsEditing(false);
  }, [editValue, onChange, onSave]);

  // Handle textarea change
  const handleTextareaChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    setEditValue(newValue);
    onChange?.(newValue);
  }, [onChange]);

  // Handle keyboard shortcuts
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Tab') {
      e.preventDefault();
      const textarea = e.currentTarget;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const newValue = editValue.substring(0, start) + '  ' + editValue.substring(end);
      setEditValue(newValue);
      onChange?.(newValue);
      
      // Set cursor position after the inserted spaces
      setTimeout(() => {
        textarea.selectionStart = textarea.selectionEnd = start + 2;
      }, 0);
    } else if (e.key === 'Escape') {
      handleEditToggle();
    } else if (e.ctrlKey && e.key === 's') {
      e.preventDefault();
      handleSave();
    }
  }, [editValue, onChange, handleEditToggle, handleSave]);

  const displayValue = isEditing ? editValue : value;

  if (!displayValue && !isEditing) {
    return (
      <div className={`flex items-center justify-center h-full text-gray-500 dark:text-gray-400 ${className}`}>
        <div className="text-center">
          <div className="text-sm">No YAML content to display</div>
          {editable && !readOnly && (
            <button
              onClick={handleEditToggle}
              className="mt-2 text-xs text-blue-600 dark:text-blue-400 hover:underline"
            >
              Click to add content
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`h-full bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-md overflow-hidden ${className}`}>
      {/* Inject CSS styles for syntax highlighting */}
      <style dangerouslySetInnerHTML={{ __html: SYNTAX_STYLES }} />
      
      {/* Header with edit controls */}
      {editable && !readOnly && (
        <div className="flex items-center justify-between px-3 py-2 bg-gray-100 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-2">
            <span className="text-xs text-gray-600 dark:text-gray-400">
              {isEditing ? 'Editing YAML' : 'Viewing YAML'}
            </span>
            {isEditing && (
              <span className="text-xs text-gray-500 dark:text-gray-500">
                (Ctrl+S to save, Esc to cancel)
              </span>
            )}
          </div>
          <div className="flex items-center space-x-1">
            {isEditing ? (
              <>
                <button
                  onClick={handleSave}
                  className="p-1 text-green-600 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/20 rounded"
                  title="Save changes (Ctrl+S)"
                >
                  <Check size={14} />
                </button>
                <button
                  onClick={handleEditToggle}
                  className="p-1 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 rounded"
                  title="Cancel editing (Esc)"
                >
                  <X size={14} />
                </button>
              </>
            ) : (
              <button
                onClick={handleEditToggle}
                className="p-1 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/20 rounded"
                title="Edit YAML"
              >
                <Edit3 size={14} />
              </button>
            )}
          </div>
        </div>
      )}
      
      <div className="flex h-full overflow-auto">
        {/* Line numbers */}
        <div className="flex-shrink-0 bg-gray-100 dark:bg-gray-800 pr-2 py-4 select-none">
          <div className="font-mono text-xs text-gray-500 dark:text-gray-400 leading-6">
            {lines.map((_, index) => (
              <div key={index} className="text-right min-w-[2rem]">
                {index + 1}
              </div>
            ))}
          </div>
        </div>

        {/* Code content with indentation guides */}
        <div className="flex-1 relative">
          {/* Indentation guides */}
          <div className="absolute inset-0 pointer-events-none">
            {lines.map((line, index) => (
              <div key={`guides-${index}`} className="relative h-6">
                {generateIndentationGuides(line, index)}
              </div>
            ))}
          </div>

          {/* Code content */}
          <div className="relative z-10">
            {isEditing ? (
              <div className="font-mono text-sm text-gray-900 dark:text-gray-100 p-4 leading-6">
                <textarea
                  ref={textareaRef}
                  value={editValue}
                  onChange={handleTextareaChange}
                  onKeyDown={handleKeyDown}
                  placeholder={placeholder}
                  className="yaml-editor-textarea"
                  spellCheck={false}
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  rows={Math.max(lines.length, 10)}
                />
              </div>
            ) : (
              <pre className="font-mono text-sm text-gray-900 dark:text-gray-100 p-4 leading-6 whitespace-pre overflow-visible">
                <code
                  dangerouslySetInnerHTML={{ __html: processedContent }}
                  className="block"
                />
              </pre>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default YamlCodeViewer;
